import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';

import { TranslateDeepService } from '../translate/translate-deep.service';
import { Message } from './message.model';
import { IMessageValue } from './message-value.model';

@Injectable({
    providedIn: 'root',
})
export class MessageService {

    private subject: Subject<Message> = new Subject();

    constructor(
        private myTranslateService: TranslateService,
        private myTranslateDeepService: TranslateDeepService,
    ) {
    }

    public successTrans(summary: string | IMessageValue, detail: string | IMessageValue, life: number = 3000, sticky: boolean = false): void {
        this.setMessage('success', this.getMessageValue(summary), this.getMessageValue(detail), life, sticky);
    }

    public infoTrans(summary: string | IMessageValue, detail: string | IMessageValue, life: number = 4000, sticky: boolean = false): void {
        this.setMessage('info', this.getMessageValue(summary), this.getMessageValue(detail), life, sticky);
    }

    public warningTrans(summary: string | IMessageValue, detail: string | IMessageValue, life: number = 5000, sticky: boolean = false): void {
        this.setMessage('warn', this.getMessageValue(summary), this.getMessageValue(detail), life, sticky);
    }

    public errorTrans(summary: string | IMessageValue, detail: string | IMessageValue, life: number = 5000, sticky: boolean = false): void {
        this.setMessage('error', this.getMessageValue(summary), this.getMessageValue(detail), life, sticky);
    }

    public success(summary: string, detail: string, life: number = 3000, sticky: boolean = false): void {
        this.setMessage('success', summary, detail, life, sticky);
    }

    public info(summary: string, detail: string, life: number = 4000, sticky: boolean = false): void {
        this.setMessage('info', summary, detail, life, sticky);
    }

    public warning(summary: string, detail: string, life: number = 5000, sticky: boolean = false): void {
        this.setMessage('warn', summary, detail, life, sticky);
    }

    public error(summary: string, detail: string, life: number = 5000, sticky: boolean = false): void {
        this.setMessage('error', summary, detail, life, sticky);
    }

    public getMessage(): Observable<Message> {
        return this.subject.asObservable();
    }

    public displayError(err: HttpErrorResponse, fallbackKey?: string): void {
        if (err == null) {
            this.error(this.myTranslateService.instant('sla-core.commons.error.label'), this.myTranslateService.instant(fallbackKey));
            return;
        }

        try {
            if (err.error && err.error.type === 'MessageException') {
                this.error(
                    `${this.myTranslateService.instant('sla-core.commons.error.label')}: ${err.error.key.slice(-4)}`,
                    this.myTranslateService.instant(err.error.key, err.error.parameters), 10000, false);
            } else {
                if (err.error.errordesc != null) {
                    this.error(this.myTranslateService.instant('sla-core.commons.error.label'), err.error.errordesc);
                } else if (err.error.message != null) {
                    this.error(this.myTranslateService.instant('sla-core.commons.error.label'), err.error.message);
                } else if (typeof err.error === 'string') {
                    if (err.error.startsWith('<!doctype html>')) {
                        throw err;
                    }

                    this.error(this.myTranslateService.instant('sla-core.commons.error.label'), err.error);
                } else {
                    this.error(this.myTranslateService.instant('sla-core.commons.error.label'), err.message);
                }

                if (err.error.titleKey && err.error.messageKey) {
                    this.error(
                        this.myTranslateDeepService.instant(err.error.titleKey, err.error.parameterMap), this.myTranslateDeepService.instant(err.error.messageKey, err.error.parameterMap));
                }
            }

        } catch (ex) {
            switch (err.status) {
                case 503:
                    this.errorTrans('sla-core.commons.error.label', 'sla-core.status-codes[503].label');
                    break;
                case 501:
                    this.errorTrans('sla-core.commons.error.label', 'sla-core.status-codes.not-implemented.label');
                    break;
                case 403:
                    this.errorTrans('sla-core.commons.error.label', 'sla-core.status-codes.forbidden.label');
                    break;
                case 404:
                    this.errorTrans('sla-core.commons.error.label', 'sla-core.status-codes.not-found.label');
                    break;
                default:
                    this.error(this.myTranslateService.instant('sla-core.commons.error.label'), `StatusCode [${err.status}]`);
                    break;
            }
        }
    }

    private setMessage(severity: string, summary: string, detail: string, life: number, sticky: boolean): void {
        this.subject.next({ severity, summary, detail, life, sticky });
    }

    private getMessageValue(message: string | IMessageValue): string {
        if (typeof message === 'string') {
            return this.myTranslateService.instant(message);
        }

        return this.myTranslateService.instant(message.value, message.translationParams);
    }

}
